/* eslint-disable complexity */
import { connect } from 'react-redux';
import SpaUtils from 'utils/Spa';
import catalogActions from 'actions/CatalogActions';
import UserActions from 'actions/UserActions';
import FrameworkUtils from 'utils/framework';
import { createSelector } from 'reselect';
import { pageSelector } from 'selectors/page/pageSelector';
import SponsoredProductsSelector from 'selectors/sponsoredProducts/sponsoredProductsSelector';
import history from 'selectors/historyLocation/historyLocationSelector';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import { PAGE_ONE } from '@ufe/shared/constants/catalog';
import rmnUtils from 'utils/rmnAndPla';
import { showPurchasedFlagOnGridPagesSelector } from 'viewModel/selectors/testTarget/showPurchasedFlagOnGridPagesSelector';
import { showUpdateDefaultSortSelector } from 'viewModel/selectors/testTarget/showUpdateDefaultSortSelector';
import {
    isAIBeautyChatEnabledPLP, isAIBeautyChatEnabledSRP, isAIBeautyGiftEnabled, isSkincareCategory
} from 'ai/utils/aiBeautyChat';
import { showSmartSkinScanBannerSelector } from 'viewModel/selectors/testTarget/showSmartSkinScanBannerSelector';
import { showPurchasedTimesSRPSelector } from 'viewModel/selectors/testTarget/showPurchasedTimesSRPSelector';
import { showPlpProductCompareSelector } from 'viewModel/selectors/testTarget/showPlpProductCompareSelector';

const { wrapHOC } = FrameworkUtils;
const { sponsoredProductsSelector } = SponsoredProductsSelector;
const { getLocalisedDataFromResource } = LanguageLocaleUtils;
const { formatSponsoredProductsClickTracker } = rmnUtils;
import { isShowSMNEnabledSelector } from 'viewModel/selectors/testTarget/showSMNSelector';
import CatalogEN from 'components/Catalog/locales/Catalog_en_US';
import CatalogFR from 'components/Catalog/locales/Catalog_fr_CA';

const getText = getLocalisedDataFromResource(CatalogEN, CatalogFR);

const fields = createSelector(
    pageSelector,
    sponsoredProductsSelector,
    history,
    showPurchasedFlagOnGridPagesSelector,
    showUpdateDefaultSortSelector,
    isShowSMNEnabledSelector,
    showSmartSkinScanBannerSelector,
    showPurchasedTimesSRPSelector,
    showPlpProductCompareSelector,
    state => state.superChat?.srpSearchTerms,
    (_state, ownProps) => ownProps.pageType,
    (_state, ownProps) => ownProps.categoryId,
    // eslint-disable-next-line max-params
    (
        page,
        sponsorProducts,
        historyLocation,
        showPurchasedFlagOnGridPages,
        showUpdateDefaultSort,
        showSMNEnabled,
        smartSkinScanBanner,
        showPurchasedTimesSRP,
        showPlpProductCompare,
        srpSearchTerms,
        pageType,
        categoryId
    ) => {
        const { pageName } = SpaUtils.getSpaTemplateInfoByTemplate(page.templateInformation?.template) || {};
        const catalog = page[pageName];
        const totalProducts = catalog?.totalProducts || 0;
        const queryParams = Object.assign({}, historyLocation.queryParams);
        const pageParamURL = Number(queryParams?.currentPage?.[0]) || undefined;
        const currentPage = catalog?.currentPage || pageParamURL || 1;
        const pageSize = catalog?.pageSize || 60;
        const categoryName = catalog.displayName;
        const parentCategoryName = catalog.parentCategory?.displayName;
        const searchKeyword = page?.search?.keyword;
        let currentTotal = currentPage * pageSize;

        if (currentTotal > totalProducts) {
            currentTotal = totalProducts;
        }

        const dontShowMoreProducts = currentTotal === totalProducts;
        const resultsText = `${totalProducts} ${getText(totalProducts === 1 ? 'result' : 'results')}`;
        const currentTotalText = `${PAGE_ONE}-${currentTotal} ${getText('of')} ${resultsText}`;
        const rootCategory = catalog?.categories?.at()?.categoryId;
        const isSkincare = isSkincareCategory(categoryId) || isSkincareCategory(rootCategory);
        const smartSkinScanBannerConfig = isSkincare ? smartSkinScanBanner : null;

        let giftFinder = false;
        let isGenAIChatPLPEnabled =
            isAIBeautyChatEnabledSRP(pageType, searchKeyword, srpSearchTerms) ||
            isAIBeautyChatEnabledPLP(categoryId) ||
            isAIBeautyChatEnabledPLP(rootCategory);

        if (!isGenAIChatPLPEnabled) {
            isGenAIChatPLPEnabled = isAIBeautyGiftEnabled(categoryId) || isAIBeautyGiftEnabled(rootCategory);
            giftFinder = isGenAIChatPLPEnabled;
        }

        const marketingTilePositions = catalog?.content?.marketingTilePositions;
        const allMarketingTiles = catalog?.content?.marketingTiles || catalog?.specialSearchTermComponents || catalog?.marketingTiles;
        // There should not be more than 5 tiles on a category page, so a 6th tile shouldn't be shown.
        // https://jira.sephora.com/browse/SMC-2093
        const marketingTiles = allMarketingTiles?.slice(0, marketingTilePositions?.length || 5);

        return {
            pageName,
            currentPage,
            products: catalog?.products?.map(product => {
                if (Sephora.configurationSettings.smnBrowseCombinedCallEnabled) {
                    return product;
                } else {
                    return formatSponsoredProductsClickTracker(product);
                }
            }),
            marketingTiles,
            marketingTilePositions,
            searchWarningMessages: catalog?.searchWarningMessages,
            searchQuery: catalog?.keyword,
            deliveryOptions: catalog?.deliveryOptions,
            sponsorProducts: showSMNEnabled && sponsorProducts?.products,
            sponsorProductsLoaded: sponsorProducts?.loaded,
            resultsText,
            dontShowMoreProducts,
            currentTotalText,
            categoryName,
            parentCategoryName,
            showPurchasedFlagOnGridPages,
            showUpdateDefaultSort,
            isGenAIChatPLPEnabled,
            giftFinder,
            totalProducts,
            resultId: catalog?.resultId,
            constructorFilters: {
                browseFilterName: catalog?.browseFilterName,
                browseFilterValue: catalog?.browseFilterValue
            },
            smartSkinScanBannerConfig,
            showSMNEnabled,
            showPurchasedTimesSRP,
            showPlpProductCompare
        };
    }
);

const functions = {
    setPageNumber: catalogActions.setPageNumber,
    fetchCompletePurchaseHistory: UserActions.fetchCompletePurchaseHistory
};

const withProductGridProps = wrapHOC(connect(fields, functions));

export {
    fields, functions, withProductGridProps
};
